import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import axios from "axios";
import produce from "immer";
import { useEffect, useState } from "react";

import { CloseIcon } from "../../../minimals/theme/overrides/CustomIcons";
import { NumberFormatCustom } from "../NumberFormat";
import { useUserContext } from "../../contexts/UserContext";
import { LedgerType, PayeeContact, PendingCheck } from "./types";

type WireType = "Incoming" | "Outgoing";

const emptyPayeeContact: PayeeContact = {
  id: "",
  name: "",
  address1: "",
  address2: "",
  city: "",
  state: "",
  zip: ""
};

const EditPendingTransactionModal = ({
  open,
  closeModal,
  orderNumber,
  transaction,
  handleRefreshList,
  handleRefreshBalance
}: {
  open: boolean;
  closeModal: () => void;
  orderNumber: string;
  transaction: LedgerType;
  handleRefreshList: () => void;
  handleRefreshBalance: () => void;
}) => {
  const user = useUserContext()?.user;
  const isWire = transaction.type === "Wire" || transaction.typeDescription?.toLowerCase().includes("wire");
  const [pendingTransaction, setPendingTransaction] = useState<PendingCheck>({
    orderNumber,
    payeeContact: emptyPayeeContact,
    payeeContactNameOnly: transaction.payorPayee || "",
    memo: transaction.memo || "",
    amount: transaction.amount?.toString() || ""
  });
  const [wireType, setWireType] = useState<WireType>(transaction.isDebit ? "Outgoing" : "Incoming");
  const [orderContacts, setOrderContacts] = useState<PayeeContact[]>([]);
  const [selectedPayeeContact, setSelectedPayeeContact] = useState<PayeeContact | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const setPendingTransactionInfo = (name: keyof PendingCheck, value: any) => {
    const newInfo = produce(pendingTransaction, (draft) => {
      draft[name] = value;
    });
    setPendingTransaction(newInfo);
  };

  const getOrderContacts = async () => {
    const { data } = await axios.get(`/proxy/api/orders/GetOrderContacts?orderNumber=${orderNumber}`);
    setOrderContacts(data);
  };

  useEffect(() => {
    getOrderContacts();
  }, []);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`/proxy/api/ledgers/${isWire ? "EditPendingWire" : "EditPendingCheck"}`, {
        orderNumber,
        transactionId: transaction.id,
        amount: Number(pendingTransaction.amount),
        payeeContact: selectedPayeeContact || emptyPayeeContact,
        payeeContactNameOnly: selectedPayeeContact ? null : pendingTransaction.payeeContactNameOnly || null,
        memo: pendingTransaction.memo,
        editedBy: user?.username?.replace(/^WORKGROUP\\/i, "") || user?.name,
        ...(isWire ? { wireType } : {})
      });
      closeModal();
      handleRefreshBalance();
      handleRefreshList();
    } finally {
      setSubmitting(false);
    }
  };

  const hasAmount = pendingTransaction.amount.trim() !== "";
  const hasPayee = Boolean(selectedPayeeContact || pendingTransaction.payeeContactNameOnly.trim());

  return (
    <Dialog open={open} onClose={closeModal} fullWidth maxWidth="sm">
      <DialogTitle>
        <Typography variant="h4">Edit Pending {isWire ? "Wire" : "Check"}</Typography>
      </DialogTitle>
      <DialogContent sx={{ paddingBottom: 3 }}>
        <IconButton
          onClick={closeModal}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500]
          }}
        >
          <CloseIcon />
        </IconButton>
        <Box paddingTop={1}>
          <Stack direction="row" spacing={2} justifyContent={isWire ? "space-between" : "flex-end"} alignItems="center">
            {isWire && <Typography variant="body2">{wireType}</Typography>}
            <TextField
              label="Amount"
              size="small"
              value={pendingTransaction.amount}
              onChange={(event) => setPendingTransactionInfo("amount", event.target.value)}
              InputProps={{
                inputComponent: NumberFormatCustom as any
              }}
            />
          </Stack>
          <Stack direction="column" spacing={2} marginTop={2}>
            <Autocomplete
              renderInput={(params) => <TextField {...params} size="small" fullWidth label="Pay to the order of" />}
              options={orderContacts}
              value={selectedPayeeContact}
              inputValue={pendingTransaction.payeeContactNameOnly}
              onInputChange={(_, inputValue, reason) => {
                if (reason === "input" || reason === "clear") {
                  setSelectedPayeeContact(null);
                  setPendingTransactionInfo("payeeContactNameOnly", inputValue);
                }
              }}
              getOptionLabel={(option: any) => (typeof option === "string" ? option : `${option.name} (${option.type})`)}
              onChange={(_, value) => {
                if (typeof value === "string") {
                  setSelectedPayeeContact(null);
                  setPendingTransactionInfo("payeeContactNameOnly", value);
                  return;
                }

                setSelectedPayeeContact(value);
                setPendingTransactionInfo("payeeContactNameOnly", value ? `${value.name} (${(value as any).type})` : "");
              }}
              freeSolo
            />
            <TextField
              label="Memo"
              size="small"
              fullWidth
              value={pendingTransaction.memo}
              onChange={(event) => setPendingTransactionInfo("memo", event.target.value)}
            />
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={closeModal} color="inherit" disabled={submitting}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting || !hasAmount || !hasPayee}>
          {submitting ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditPendingTransactionModal;
